import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ───────────────────────────────────────────────────────────────────

export type BenachrichtigungPrio = 'low' | 'medium' | 'high' | 'critical';

export type BenachrichtigungTyp =
  | 'verjährung_kritisch'
  | 'verjährung_warn'
  | 'stellungnahmefrist_kritisch'
  | 'stellungnahmefrist_warn'
  | 'qs_erforderlich'
  | 'qs_korrektur'
  | 'kuerzung_eingegangen'
  | 'zahlung_teilweise'
  | 'abtretung_fehlt';

export interface Benachrichtigung {
  id: string;
  empfaenger_id: string;
  erstellt_am: string;
  prio: BenachrichtigungPrio;
  typ: string;
  titel: string;
  body: string;
  akte_id: string | null;
  aktion_url: string | null;
  gelesen_am: string | null;
  erledigt_am: string | null;
  snooze_bis: string | null;
}

export interface BenachrichtigungFilter {
  typ?: string;
  prio?: BenachrichtigungPrio;
  von?: string;
  bis?: string;
  nurUngelesen?: boolean;
}

// ─── Pure helpers (exported for tests) ───────────────────────────────────────

export function prioSortOrder(p: BenachrichtigungPrio | string): number {
  switch (p) {
    case 'critical': return 4;
    case 'high':     return 3;
    case 'medium':   return 2;
    case 'low':      return 1;
    default:         return 0;
  }
}

export function isOverdue(b: Pick<Benachrichtigung, 'snooze_bis'>): boolean {
  if (!b.snooze_bis) return true;
  return new Date(b.snooze_bis) <= new Date();
}

const PRIO_STYLES: Record<string, { dot: string; badge: string; border: string }> = {
  critical: {
    dot:    'bg-red-500',
    badge:  'bg-red-100 text-red-700',
    border: 'border-l-red-500',
  },
  high: {
    dot:    'bg-orange-500',
    badge:  'bg-orange-100 text-orange-700',
    border: 'border-l-orange-500',
  },
  medium: {
    dot:    'bg-yellow-400',
    badge:  'bg-yellow-50 text-yellow-700',
    border: 'border-l-yellow-400',
  },
  low: {
    dot:    'bg-blue-400',
    badge:  'bg-blue-50 text-blue-700',
    border: 'border-l-blue-400',
  },
};

export function prio(p: string) {
  return PRIO_STYLES[p] ?? PRIO_STYLES['low'];
}

const TYP_LABEL: Record<string, string> = {
  verjährung_kritisch:        'Verjährung',
  verjährung_warn:            'Verjährung',
  stellungnahmefrist_kritisch:'Stellungnahmefrist',
  stellungnahmefrist_warn:    'Stellungnahmefrist',
  qs_erforderlich:            'QS ausstehend',
  qs_korrektur:               'QS Korrektur',
  kuerzung_eingegangen:       'Kürzung',
  zahlung_teilweise:          'Teilzahlung offen',
  abtretung_fehlt:            'Abtretung fehlt',
};

export function typLabel(typ: string): string {
  return TYP_LABEL[typ] ?? typ;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBenachrichtigungen(filter?: BenachrichtigungFilter) {
  const { profile, impersonateUserId } = useAuth();
  const effectiveUserId = impersonateUserId || profile?.id;

  const [benachrichtigungen, setBenachrichtigungen] = useState<Benachrichtigung[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!effectiveUserId) return;
    setLoading(true);
    try {
      let q = supabase
        .from('benachrichtigungen')
        .select('*')
        .eq('empfaenger_id', effectiveUserId)
        .order('erstellt_am', { ascending: false })
        .limit(200);

      if (filter?.typ)          q = q.eq('typ', filter.typ);
      if (filter?.prio)         q = q.eq('prio', filter.prio);
      if (filter?.von)          q = q.gte('erstellt_am', filter.von);
      if (filter?.bis)          q = q.lte('erstellt_am', filter.bis);
      if (filter?.nurUngelesen) q = q.is('gelesen_am', null);

      const { data, error } = await q;
      if (error) throw error;
      setBenachrichtigungen((data ?? []) as Benachrichtigung[]);
    } catch (err) {
      console.error('Fehler beim Laden der Benachrichtigungen:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, filter?.typ, filter?.prio, filter?.von, filter?.bis, filter?.nurUngelesen]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime subscription — filtered to own empfaenger_id
  useEffect(() => {
    if (!effectiveUserId) return;

    const channel = supabase
      .channel(`benachrichtigungen:${effectiveUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'benachrichtigungen',
          filter: `empfaenger_id=eq.${effectiveUserId}`,
        },
        (payload) => {
          setBenachrichtigungen(prev => [payload.new as Benachrichtigung, ...prev]);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'benachrichtigungen',
          filter: `empfaenger_id=eq.${effectiveUserId}`,
        },
        (payload) => {
          setBenachrichtigungen(prev =>
            prev.map(b => b.id === (payload.new as Benachrichtigung).id
              ? (payload.new as Benachrichtigung)
              : b,
            ),
          );
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [effectiveUserId]);

  const markAsRead = useCallback(async (id: string) => {
    const ts = new Date().toISOString();
    await supabase
      .from('benachrichtigungen')
      .update({ gelesen_am: ts })
      .eq('id', id);
    setBenachrichtigungen(prev =>
      prev.map(b => b.id === id ? { ...b, gelesen_am: ts } : b),
    );
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!effectiveUserId) return;
    const ts = new Date().toISOString();
    await supabase
      .from('benachrichtigungen')
      .update({ gelesen_am: ts })
      .eq('empfaenger_id', effectiveUserId)
      .is('gelesen_am', null);
    setBenachrichtigungen(prev =>
      prev.map(b => b.gelesen_am ? b : { ...b, gelesen_am: ts }),
    );
  }, [effectiveUserId]);

  const snooze = useCallback(async (id: string, dauer: '1h' | '1d' | '1w') => {
    const { error } = await supabase.rpc('benachrichtigung_snoozen', {
      p_id: id,
      p_dauer: dauer,
    });
    if (!error) {
      const ms = dauer === '1h' ? 3_600_000 : dauer === '1d' ? 86_400_000 : 604_800_000;
      const snooze_bis = new Date(Date.now() + ms).toISOString();
      setBenachrichtigungen(prev =>
        prev.map(b => b.id === id ? { ...b, snooze_bis } : b),
      );
    }
  }, []);

  const sichtbar = benachrichtigungen.filter(b => !b.gelesen_am && isOverdue(b));
  const hasCritical = sichtbar.some(b => b.prio === 'critical');
  const unreadCount = sichtbar.length;

  return {
    benachrichtigungen,
    sichtbar,
    loading,
    unreadCount,
    hasCritical,
    markAsRead,
    markAllAsRead,
    snooze,
    reload: load,
  };
}
